import { z } from 'zod';
import type { components } from '@/api/generated/schema';
import { ORG_ACCESS_STATES } from '@/components/billing/planView';
import type { TrialOffer } from '@/types/billing';

/**
 * Значения признака «пробный положен» (WEB-4180) — ровно перечень `TrialOffer`
 * контракта: лишнее значение не соберётся (`satisfies`), недостающее ловит
 * проверка `_TrialOffersComplete` ниже.
 */
export const TRIAL_OFFERS = ['offered', 'used_by_you', 'used_by_org'] as const satisfies readonly TrialOffer[];
type _TrialOffersComplete = Exclude<TrialOffer, (typeof TRIAL_OFFERS)[number]> extends never ? true : never;
const _trialOffersComplete: _TrialOffersComplete = true;
void _trialOffersComplete;

/**
 * Zod schema for `GET /billing` (WEB-575, contract FE<->BE layer 2).
 *
 * `z.ZodType<BillingOverviewResponseDto>` pins this schema to the codegen
 * type (layer 1) — if the backend DTO renames/removes a required field,
 * `openapi-typescript` regenerates the type and THIS file fails to compile
 * (missing/extra required property), not just some far-away consumer
 * component. That is deliberate: it is what makes "codegen types are
 * actually used" true rather than aspirational.
 */
export type BillingOverviewResponseDto =
  components['schemas']['BillingOverviewResponseDto'];

const subscriptionSummarySchema: z.ZodType<
  components['schemas']['SubscriptionSummaryResponseDto']
> = z.object({
  tier: z.enum(['FREE', 'PRO']),
  status: z.enum([
    'ACTIVE',
    'PAST_DUE',
    'CANCELED',
    'INCOMPLETE',
    'INCOMPLETE_EXPIRED',
    'TRIALING',
    'UNPAID',
    'PAUSED',
  ]),
  currentPeriodEnd: z.string().nullable(),
  cancelAtPeriodEnd: z.boolean(),
  stripeCustomerId: z.string().nullable(),
  stripeSubscriptionId: z.string().nullable(),
  // WEB-1487: конец льготного окна публичной отдачи при PAST_DUE (null вне
  // эпизода просрочки); z.object молча выбрасывает неописанные ключи (урок
  // WEB-1080/funnels), так что без этой строки дата не доехала бы до кабинета.
  //
  // ОБЯЗАТЕЛЬНО .optional() (ревизия WEB-1584, приёмка): тот же класс, что у
  // payFromBalanceOnCardFailEnabled ниже — фронт и бэк выкатываются порознь,
  // и строгое поле роняло бы ВЕСЬ раздел «Баланс и тариф» валидацией на
  // бэке, который его ещё не отдаёт (или откатился без него). Отсутствие
  // поля читается как «льготного окна нет» (потребители делают `?? null`).
  publicGraceUntil: z.string().nullable().optional(),
});

const tierLimitsSchema: z.ZodType<
  components['schemas']['TierLimitsResponseDto']
> = z.object({
  maxProjects: z.number(),
  maxQuizzes: z.number(),
  maxPaywalls: z.number(),
  maxFunnels: z.number(),
  maxCustomDomains: z.number(),
  publishAllowed: z.boolean(),
  // WEB-647: optional — старый бэк без поля не должен ломать parse (safe default).
  // Синк с прод-Swagger (регенерация 15b2f99b): поле стало обязательным.
  smartLinksAllowed: z.boolean(),
  includedQuizStarts: z.number(),
  overageCentsPer1k: z.number(),
});

const tierUsageSchema: z.ZodType<
  components['schemas']['TierUsageResponseDto']
> = z.object({
  projects: z.number(),
  quizzes: z.number(),
  paywalls: z.number(),
  // WEB-1080: счётчик воронок, по которому на бесплатном тарифе считается
  // общая квота «квизы + пейволлы». Схема о нём не знала, потому что описание
  // контракта не перегенерировали (WEB-1119) — а `z.object` молча выбрасывает
  // всё, чего нет в описании, поэтому на экране расхода шкала показывала 0
  // вместо реального числа.
  //
  // Обязательное — как в контракте: сервер заполняет его на ОБОИХ путях
  // (billing.service.ts у пустого кабинета и tier-enforcement.service.ts у
  // настоящего подсчёта), пропустить его он не может. Строгость здесь и есть
  // сторож: если поле однажды исчезнет из ответа, разбор скажет об этом
  // громко, а не покажет молча ноль, как было до WEB-1119.
  funnels: z.number(),
  quizStarts: z.number(),
  includedQuizStarts: z.number(),
  inOverage: z.boolean(),
  overageUnits: z.number(),
  estimatedOverageCents: z.number(),
});

/**
 * WEB-665 (round 3): a single wallet balance lot (grant / top-up /
 * adjustment). WEB-767: бэкенд-поле давно приземлилось и обязательное
 * («Always an array», billing.dto.ts) — optional снят при регенерации схемы.
 */
const walletLotSchema = z.object({
  id: z.string(),
  label: z.string(),
  // WEB-1874 этап 4 ч.2: машинный код подписи рядом со старым label (+ параметры)
  //
  // ОБЯЗАТЕЛЬНО .optional() (ревизия пачки 16, находка Ф5): контракт объявляет
  // код обязательным, и сегодняшний сервер его всегда отдаёт, — но фронт и
  // бэк выкатываются порознь, и в окно выката (а также при откате бэка)
  // приходит СТАРЫЙ ответ без кода. Строгое поле бракует такой ответ целиком:
  // раздел «Баланс и тариф» не открывается вовсе, а написанная страховка
  // «нет кода — печатаем прежнее поле label» (WalletBalanceCard, сторож
  // WalletLotLabel.web1874) становится недостижимой. Тот же класс, что у
  // publicGraceUntil выше.
  labelCode: z
    .enum([
      'TRIAL_GRANT',
      'ONBOARDING_GRANT',
      'ADMIN_REGISTER_GRANT',
      'FIRST_PAYMENT_GRANT',
      'TOPUP_BONUS',
      'TIER_GRANT',
      'GRANT',
      'TOPUP',
      'REFUND',
      'ADMIN_DESCRIPTION',
      'ADJUSTMENT',
    ])
    .optional(),
  // Параметры подписи давно необязательные: у половины кодов их нет.
  labelParams: z.record(z.string(), z.string()).optional(),
  remainingCents: z.number(),
  expiresAt: z.string().nullable(),
  source: z.enum(['grant', 'topup', 'adjustment']),
});

/**
 * WEB-4008: условия тарифа из действующей версии ценового плана организации
 * и ход пробного. Все поля внутри — полностью и строго (контракт), а сами
 * поля обзора — `.nullable().optional()`: сервер и кабинет выкатываются
 * порознь (класс WEB-1119). Суммы кабинет только печатает — не считает.
 */
type Schemas = components['schemas'];

const planTermsSchema: z.ZodType<Schemas['PlanTermsResponseDto']> = z.object({
  versionId: z.string(),
  currency: z.literal('usd'),
  tariff: z.object({
    monthlyCents: z.number(),
    monthlyChargeCents: z.number(),
    annualMonthlyCents: z.number(),
    annualChargeCents: z.number(),
    annualSavingsCents: z.number(),
  }),
  trial: z.object({ days: z.number(), exitLimit: z.number() }),
  usage: z.object({
    freeExitsPerMonth: z.number(),
    chargeThresholdCents: z.number(),
    exitTiers: z.array(
      z.object({ upTo: z.number().nullable(), rateMicros: z.number() }),
    ),
    revenueTiers: z.array(
      z.object({ upToCents: z.number().nullable(), rateBps: z.number() }),
    ),
    billingStartsAt: z.string().nullable(),
  }),
});

const trialProgressSchema: z.ZodType<Schemas['TrialProgressResponseDto']> =
  z.object({
    endsAt: z.string().nullable(),
    daysLeft: z.number().nullable(),
    exitsUsed: z.number(),
    exitsLeft: z.number(),
  });

/**
 * Ревизия пачки 16 (находка Ф5): разбор ответа ДОПУСКАЕТ лот без машинного
 * кода подписи (окно выката, откат бэка), поэтому пришпиливаем схему не к
 * самому контракту, а к нему же с одним ослабленным полем. Ослабление
 * выводится ИЗ КОДГЕНА (`WalletLotResponseDto['labelCode']`), а не переписано
 * руками: переименуй бэкенд это поле — тип перестанет компилироваться здесь,
 * ровно как задумано шапкой файла.
 */
type WalletLotLenient = Omit<
  components['schemas']['WalletLotResponseDto'],
  'labelCode'
> & {
  labelCode?: components['schemas']['WalletLotResponseDto']['labelCode'];
};

type BillingOverviewLenient = Omit<BillingOverviewResponseDto, 'lots'> & {
  lots: WalletLotLenient[];
};

export const billingOverviewSchema: z.ZodType<BillingOverviewLenient> =
  z.object({
    // WEB-1120 (owner-ратиф. 10.08, «закрыть тонко»): null для читателя без
    // canManageBilling (участник; админ с выключенным billingAccess,
    // WEB-1093) — сервер отдаёт null вместо тарифа/подписки, остаток
    // (balanceCents ниже) при этом отдаёт всегда.
    subscription: subscriptionSummarySchema.nullable(),
    tier: z.enum(['FREE', 'PRO']).nullable(),
    limits: tierLimitsSchema,
    usage: tierUsageSchema,
    balanceCents: z.number(),
    // WEB-2826 (сервер — WEB-2837): свободный остаток (порции минус резервы
    // идущих действий ИИ) и сам резерв. Без этих строк z.object молча
    // выбросил бы оба поля (урок WEB-1119), и кабинет сверял бы трату с
    // суммой порций, а сервер — со свободным остатком (класс WEB-1242).
    // .optional() — фронт и бэк выкатываются порознь (тот же класс, что у
    // publicGraceUntil); отсутствие читается одной дверью
    // utils/walletAvailable.ts как «свободно всё, резерва нет».
    availableCents: z.number().optional(),
    heldCents: z.number().optional(),
    // Optional: not yet on the generated BillingOverviewResponseDto — this is
    // a deliberate, backward-compatible superset (see file-level doc above
    // WEB-767: поле обязательное (бэкенд всегда отдаёт массив).
    lots: z.array(walletLotSchema),
    // WEB-754 (синк с регенерацией схемы при WEB-839): frozen — аккаунт
    // заморожен по chargeback (error-баннер), downgraded — одноразовый признак
    // принудительного понижения тарифа (warning-баннер, гаснет по ack).
    frozen: z.boolean(),
    downgraded: z.boolean(),
    // WEB-1094 (деньги): переключатель «оплачивать тариф с баланса, если
    // карта не прошла» (default false) + персистентная плашка «оплачено с
    // баланса, обновите карту» (держится до следующего успешного платежа
    // картой — НЕ одноразовый ack, в отличие от downgraded выше).
    //
    // ОБЯЗАТЕЛЬНО .optional() (приёмка): фронт и бэк выкатываются порознь, и
    // строгое поле роняло бы ВЕСЬ экран биллинга валидацией на бэке, который
    // этих полей ещё не отдаёт, — ровно класс WEB-1119. Отсутствие поля
    // читается как false (потребители и так делают `?? false`).
    payFromBalanceOnCardFailEnabled: z.boolean().optional(),
    paidFromBalanceBanner: z.boolean().optional(),
    // ПОВТОРНЫЙ QA-возврат 25.08 (WEB-1126 + WEB-1290): явный признак «платёжная
    // часть скрыта настройками доступа». Без строки ЗДЕСЬ признак молча
    // выбрасывается разбором (z.object отбрасывает неописанные ключи, урок
    // WEB-1119) — экран снова показывал бы пустоту без объяснения, а тесты
    // хранилища и компонентов оставались бы зелёными: они кладут ответ в срез
    // напрямую, минуя разбор. Сторож — billing.schema.test.ts.
    // .optional() по той же причине, что у двух полей выше: фронт и бэк
    // выкатываются порознь, отсутствие читается как false.
    billingHidden: z.boolean().optional(),
    // WEB-4008: без этих строк z.object молча выбросил бы оба поля (урок
    // WEB-1119); `.optional()` — фронт и бэк выкатываются порознь.
    planTerms: planTermsSchema.nullable().optional(),
    trialProgress: trialProgressSchema.nullable().optional(),
    // WEB-3302 (сервер — WEB-2502): состояние доступа организации — по нему
    // решает вид тарифа (components/billing/planView.ts). Без строки ЗДЕСЬ
    // z.object выбросил бы поле (урок WEB-1119), и вид у всех был бы
    // «unknown». Значения — ключи таблицы правила вида (ORG_ACCESS_STATES,
    // равны перечню OrgAccessState контракта — сторож planView.test.ts).
    // .optional() — фронт и бэк выкатываются порознь; .catch(null) — значение,
    // которого кабинет ещё не знает (сервер новее), читается как «не знаем»,
    // а не роняет разбор и с ним весь раздел «Баланс/Тариф».
    accessState: z.enum(ORG_ACCESS_STATES).nullable().optional().catch(null),
    // WEB-4180 (возврат 1): признак «пробный положен» — по нему окно и блок
    // «до пробного» выбирают «Начните пробный период» или «Оформить тариф» со
    // строкой «спишем <сумма>». Без строки ЗДЕСЬ z.object выбрасывал поле
    // (урок WEB-1119), и каждый видел обещание немедленного списания, хотя
    // оформление давало пробный. .optional() и .catch(null) — как у accessState.
    trialOffer: z.enum(TRIAL_OFFERS).nullable().optional().catch(null),
  });
